import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { queryKeys } from '@core/api/queryKeys';

/**
 * The one discriminator the pricing page's CTA table keys off (BE `SubscriptionResponse.planState`,
 * 2026-08-16). The server resolves it once, from the row + any active override + the clock, so the
 * browser does a table lookup on `(planState, card.tier)` instead of rebuilding boolean algebra out
 * of `source`/`status`/`isPurchasable` — the exact mix that told a Pro subscriber to "Get started"
 * on Pro and "Coming soon" about Free.
 *
 * - `free` — the Core floor: no Stripe subscription behind the row (or a paid-Core plan).
 * - `proActive` — paid, running, renewing. `renewsAt` is set.
 * - `proCancelling` — paid, running, but ending at `endsAt`. **Still Pro** until then; resumable.
 * - `proPastDue` — the card failed. Effective tier is Core while Stripe retries.
 * - `lapsed` — the Stripe subscription ended (or the clock passed the period end). Core.
 * - `comped` — an admin override above the paid tier. Nothing here is Stripe's to change.
 *
 * A DECLINED comp (2026-08-16) deliberately gets no state of its own: declining changes neither the
 * end date nor the "is this comp active" predicate, so the caller is still `comped`, still Pro, to
 * the exact second the comp already had. The decline is a second, orthogonal fact — `compDeclined`
 * below — which is why it is a flag on the row and not a seventh member here.
 */
export type SubscriptionPlanState =
  | 'free'
  | 'proActive'
  | 'proCancelling'
  | 'proPastDue'
  | 'lapsed'
  | 'comped';

/**
 * The caller's own effective subscription — mirrors the BE `SubscriptionResponse` (camelCase,
 * enums as camelCase strings). `null` on `UserProfile` means the feature is dark (`Stripe:Enabled`
 * is false) — and since 2026-08-16 that is the ONLY thing it means: a user whose
 * `v2_subscriptions` row is missing gets a synthesized free plan from the BE (plus a server-side
 * Error log), never a 404 and never a 403.
 *
 * No `isTrial` / no `trialing` status — the 7-day trial was removed 2026-08-13 in favor of a free
 * base tier (`tier: 'core'`, display name "Free" from the plans catalog).
 *
 * `tier` is the **effective** tier (paid tier raised by any active override), which is why the
 * "Your plan" badge and the CTA can both derive from it and never contradict each other.
 */
export interface MeSubscription {
  /** EFFECTIVE tier — `max(paid tier, active override tier)`. The badge and the CTA both read this. */
  tier: 'core' | 'pro';
  /**
   * Raw Stripe-mirrored status. `'cancelled'` is a LEGACY value: the BE stopped writing it
   * 2026-08-16 (cancellation-in-progress is `active` + `cancelAtPeriodEnd`, cancellation-complete is
   * `expired`) but deliberately did not delete the enum, because it is persisted as text and old
   * rows may still carry it. Nothing in this app branches on `status` — use `planState`.
   */
  status: 'active' | 'pastDue' | 'cancelled' | 'expired';
  /** Kept for display/diagnostics only. `'trial'` is legacy, same reasoning as `'cancelled'`.
   *  **Never branch a CTA on this** — that is what produced the 2026-08-16 bug. */
  source: 'trial' | 'stripe' | 'manual';
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  /** LEGACY — always `true` while a subscription exists. Kept one release for compatibility; the
   *  Core floor replaced the all-or-nothing gate, so **nothing may branch on it**. */
  isEntitled: boolean;
  isOverride: boolean;
  /** The CTA discriminator — see `SubscriptionPlanState`. */
  planState: SubscriptionPlanState;
  /** Set only for `proActive`; the 2099 free-tier sentinel never reaches the wire. */
  renewsAt: string | null;
  /** When Pro stops (`proCancelling`) or when a comp expires (`comped`); null everywhere else. */
  endsAt: string | null;
  /** Server-computed: whether `POST /billing/resume` will be accepted. Never a browser-clock guess. */
  canResume: boolean;
  /**
   * Server-computed: whether `POST /billing/comp/decline` will be accepted — i.e. `comped`, with a
   * non-null comp expiry, not already declined. The `canResume` rule applies verbatim: never a
   * browser-clock guess, and never re-derived from `endsAt` here.
   *
   * **False for an UNDATED comp** (`expires_at IS NULL` — a never-expiring grant). There is no date
   * for the account to fall to Free on, so there is nothing to decline, and the Free card keeps
   * reading "Managed by Optimus" — the pre-2026-08-16 behaviour, still correct for that one shape.
   */
  canDeclineComp: boolean;
  /**
   * The comp has been declined: it will run out and NOT be re-granted. `planState` stays `'comped'`
   * and `endsAt` still carries the comp's own `expires_at` — declining changes **neither**, per the
   * user's rule (*"cancel, means that there will be no renewal and it will move to free tier"*): Pro
   * runs to the exact second the comp already had, then the account falls to Free. Never an
   * immediate revocation, and undoable with "Keep Pro" (`POST /billing/comp/keep`) for as long as the
   * comp is still active.
   */
  compDeclined: boolean;
  /** Whether reaching Pro means `change-plan` (true) or a fresh `checkout-session` (false). */
  hasLiveStripeSubscription: boolean;
  /** Whether `POST /billing/portal` will succeed — a real Stripe customer id exists. */
  manageBillingAvailable: boolean;
}

export interface UserProfile {
  id: string;
  email: string;
  cognitoUsername: string;
  firstName: string | null;
  lastName: string | null;
  phoneNumber: string | null;
  timeZone: string;
  createdAt: string;
  approvalStatus: 'pending' | 'approved' | 'rejected';
  /** Per-user action flags (v2_user_action_flags) — drive first-time prompts. See ACTION_FLAGS. */
  actionFlags: string[];
  /** Null ONLY when the billing feature is dark (`Stripe:Enabled=false`) — see `MeSubscription`. */
  subscription: MeSubscription | null;
  /**
   * Plan My Day (2026-09-09): the user's Start / End of Trading Day as `"HH:mm"` WALL-CLOCK time in
   * `timeZone` — the boundary the day lock, the two reminder emails and the streak day are computed
   * from. The API serializes with `WhenWritingNull`, so an UNSET time arrives as an **absent key**,
   * not `null`: always read these with `?? ''` (form state) or `?? null` (wire), never `!`.
   */
  tradingDayStart?: string | null;
  tradingDayEnd?: string | null;
  /** Reminder-email opt-ins (spec §6C). Always present; both default to `true` on the BE. */
  planEmailEnabled: boolean;
  reviewEmailEnabled: boolean;
}

/**
 * Well-known per-user action-flag keys (BE: UserActionFlagKeys). Presence in
 * `UserProfile.actionFlags` means the user has performed / been through that action.
 * Only `cqgConnectPromptSeen` is client-settable (useSetActionFlag) — the rest are server-derived.
 */
export const ACTION_FLAGS = {
  /** Server-set on the user's first successful CQG logon. */
  cqgConnected: 'cqg_connected',
  /** Set when the user dismisses the first-time connect-CQG banner. */
  cqgConnectPromptSeen: 'cqg_connect_prompt_seen',
  /** Account-sync workflow: the concurrent-session request email went to Optimus support. */
  concurrentRequestSubmitted: 'concurrent_request_submitted',
  /** Account-sync workflow: admin marked the concurrent login as established (backoffice). */
  concurrentLoginEstablished: 'concurrent_login_established',
} as const;

export function useMe() {
  const api = useApiClient();
  return useQuery({
    queryKey: queryKeys.me,
    // Shorter critical-path timeout than the default: /me gates the whole app boot, so if the backend is
    // unreachable we want it to fail fast → ApprovalGuard shows a recovery screen instead of an endless
    // "Signing you in…" splash.
    queryFn:  () => api.get<UserProfile>('/api/v1/me', { timeoutMs: 10_000 }),
    staleTime: 60_000,
  });
}
